import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import {
  HOTSPOT_SYS_ICONS,
  hsRecentSettings,
  reAddEditingHotspot,
} from '@modules/hotspot';
import { $editingHotspotApi } from '@stores/editingHotspotStores';
import {
  type CustomFeature,
  FeatureType,
  HSIconType,
  type Hotspot,
  SceneType,
} from '@types';
import { customButtonDefault } from '../../edit';
import { type CFSetData, CustomFeatureProvider } from '../CustomFeatureContext';
import FeatureSwitch from '../components/FeatureSwitch';
import AdvNextSceneSetting from '../customFeatureComponents/AdvNextSceneSetting';
import AdvSceneSetting from '../customFeatureComponents/AdvSceneSetting';
import { DisplaySetting } from './DisplaySetting';
import IconSetting from './IconSetting';
import { PartnerIDSetting } from './PartnerIDSetting';
import TitleSetting from './TitleSetting';

const items: DropdownItem<FeatureType>[] = [
  { id: FeatureType.scene, name: '场景切换' },
  { id: FeatureType.prevScene, name: '上一场景' },
  { id: FeatureType.nextScene, name: '下一场景' },
  { id: FeatureType.link, name: '超链接' },
  { id: FeatureType.mobile, name: '电话' },
  { id: FeatureType.map, name: '导航' },
  { id: FeatureType.media, name: '图文' },
  { id: FeatureType.text, name: '文本' },
  { id: FeatureType.article, name: '文章' },
  { id: FeatureType.doc, name: 'PDF' },
  { id: FeatureType.audio, name: '音频' },
  { id: FeatureType.video, name: '视频' },
  { id: FeatureType.miniProgram, name: '小程序' },
  { id: FeatureType.model3d, name: '3D模型' },
  { id: FeatureType.seqFrame, name: '逐帧环物' },
  { id: FeatureType.event, name: '事件' },
  { id: FeatureType.noFunc, name: '无功能' },
];

const getTitlePlaceholder = (code: FeatureType) => {
  if (code === FeatureType.prevScene) {
    return '请输入热点标题，为空时默认为上一场景的场景名称';
  }
  if (code === FeatureType.nextScene) {
    return '请输入热点标题，为空时默认为下一场景的场景名称';
  }
};

export function HotspotBasicSettings({
  hotspot,
  sceneType,
}: { hotspot: Hotspot; sceneType?: SceneType }) {
  const feat = hotspot.data;
  const setFeat: CFSetData<CustomFeature> = (cb) => {
    $editingHotspotApi.update((draft) => {
      cb(draft.data);
    });
  };

  const handleFeatChange = (code: FeatureType) => {
    $editingHotspotApi.update((draft) => {
      draft.data = customButtonDefault(code);
      if (
        draft.data.code === FeatureType.scene &&
        hsRecentSettings.featureScene
      ) {
        draft.data.animation = hsRecentSettings.featureScene.animation;
      }
      const { icon } = draft;
      if (icon.type === HSIconType.system) {
        icon.url = HOTSPOT_SYS_ICONS[code].url;
        icon.frame = HOTSPOT_SYS_ICONS[code].frame;
      }
      // if (
      //   icon.type === HSIconType.mark &&
      //   icon.media?.type === MediaType.system
      // ) {
      //   icon.media.url = HOTSPOT_MARK_ICONS[code];
      // }
    });
    reAddEditingHotspot();
  };

  return (
    <CustomFeatureProvider setData={setFeat} data={feat}>
      <Collapse title="热点类型">
        <Dropdown
          items={items}
          selected={feat.code}
          onChange={handleFeatChange}
        />
      </Collapse>
      <IconSetting />
      {/* 切换热点标题位置特殊处理 */}
      {feat.code !== FeatureType.scene && (
        <>
          <TitleSetting placeholder={getTitlePlaceholder(feat.code)} />
          {sceneType === SceneType.panoVideo && <DisplaySetting />}
        </>
      )}
      <FeatureSwitch code={feat.code} />
      {feat.code === FeatureType.scene && (
        <AdvSceneSetting
          hotspot={hotspot}
          onTargetNameChange={(newName) => {
            $editingHotspotApi.update((draft) => {
              draft.title = newName;
            });
          }}
        />
      )}
      {feat.code === FeatureType.scene && (
        <>
          <TitleSetting />
          {sceneType === SceneType.panoVideo && <DisplaySetting />}
        </>
      )}
      {feat.code === FeatureType.prevScene && (
        <AdvNextSceneSetting hotspot={hotspot} reverse />
      )}
      {feat.code === FeatureType.nextScene && (
        <AdvNextSceneSetting hotspot={hotspot} />
      )}
      <PartnerIDSetting />
    </CustomFeatureProvider>
  );
}
